"use client";

import { toast as sonnerToast, Toaster } from "sonner";

// Re-export Sonner's Toaster component
export { Toaster };

// Create a wrapper for the existing toast API
interface ToastOptions {
  type?: "success" | "error" | "warning" | "info";
  title: string;
  description?: string;
}

export function showToast({ type = "success", title, description }: ToastOptions) {
  switch (type) {
    case "success":
      sonnerToast.success(title, {
        description,
      });
      break;
    case "error":
      sonnerToast.error(title, {
        description,
      });
      break;
    case "warning":
      sonnerToast.warning(title, {
        description,
      });
      break;
    case "info":
      sonnerToast.info(title, {
        description,
      });
      break;
  }
}

// Create a hook that matches the existing API
export function useToast() {
  return {
    showToast,
    toast: showToast, // Backward compatibility
    hideToast: () => {
      // Sonner handles auto-dismiss, but we can add manual dismiss if needed
      sonnerToast.dismiss();
    },
  };
}

// Legacy provider for backward compatibility.
//
// Sonner's <Toaster> defaults its container to z-index 9999, which lands BELOW
// our drawers/sheets (z-drawer = 80 → resolved high by Radix portals) and other
// open overlays, so toasts could hide under them. We pin the toast container to
// the shared `z-toast` token (70) — the top of the named scale, above modal (50),
// popover (60), and drawer (80 in-tree but portaled). The `z-toast` class sets
// the value via Tailwind; we mirror it in `style` because Sonner renders its own
// container element and applies an inline z-index that a class alone may not win
// against. The numeric literal here is the resolved `z-toast` token value, not a
// magic number.
const Z_TOAST = 70; // packages/shared-config/tailwind-z-index.cjs → toast

export function ToastProvider({ children }: { children: React.ReactNode }) {
  return (
    <>
      {children}
      <Toaster
        position="top-right"
        richColors
        closeButton
        duration={5000}
        className="z-toast"
        // z-index-allow: resolved value of the shared `z-toast` token (see Z_TOAST);
        // Sonner applies its own inline z-index, so the class needs an inline mirror to win.
        style={{ zIndex: Z_TOAST }}
      />
    </>
  );
}
